import React, { useRef, memo, useEffect, MouseEvent } from 'react';
import { useThrottleFn, LucaResizeObserver, getClientRectInfo } from '@/sub-luca-plugin/utils';
import classNames from 'classnames';
import './index.scss';
import { tCommon } from '@/sub-luca-plugin/i18n';

interface IProps {
    lineHeight: number;
    maxLine: number;
    content: string;
    className?: string;
}

const ExpandableOrEditText: React.FC<IProps> = ({ content, lineHeight, maxLine, className }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const hideContainerRef = useRef<HTMLDivElement | null>(null);
    const openRef = useRef<HTMLDivElement | null>(null);
    const closeRef = useRef<HTMLDivElement | null>(null);
    const showOpen = useRef(false);

    const handleExpand = (type: 'open' | 'close', ev: MouseEvent<HTMLDivElement>) => {
        ev.stopPropagation();
        if (type === 'open') {
            containerRef.current!.style.webkitLineClamp = '';
            openRef.current!.style.display = 'none';
            closeRef.current!.style.display = 'flex';
        } else {
            containerRef.current!.style.webkitLineClamp = `${maxLine}`;
            openRef.current!.style.display = 'flex';
            closeRef.current!.style.display = 'none';
        }
    };

    const { run: setEllipsisByContainerWidthChange } = useThrottleFn(
        async () => {
            if (!openRef.current || !closeRef.current) return;
            const { clientHeight } = await getClientRectInfo(hideContainerRef.current!);
            const currentLine = clientHeight / lineHeight;
            // console.log(currentLine, maxLine);
            if (currentLine > maxLine) {
                if (containerRef.current!.style.webkitLineClamp === '') {
                    openRef.current!.style.display = 'none';
                    closeRef.current!.style.display = 'flex';
                } else {
                    openRef.current!.style.display = 'flex';
                    closeRef.current!.style.display = 'none';
                }
            } else {
                openRef.current!.style.display = 'none';
                closeRef.current!.style.display = 'none';
            }
        },
        { wait: 300 }
    );

    const setEllipsisByInit = async (el: HTMLDivElement | null) => {
        // console.log('setEllipsisByInit', el);
        if (hideContainerRef.current) return;
        hideContainerRef.current = el;
        const { clientHeight } = await getClientRectInfo(el);
        const currentLine = clientHeight / lineHeight;
        // console.log(currentLine, maxLine);
        if (currentLine > maxLine) {
            showOpen.current = true;
            containerRef.current!.style.webkitLineClamp = `${maxLine}`;
        }
    };

    useEffect(() => {
        const hideContainer = hideContainerRef.current!;
        const resizeObserver = new LucaResizeObserver(setEllipsisByContainerWidthChange);
        resizeObserver.observe(hideContainer);
        return () => {
            resizeObserver.unobserve(hideContainer);
        };
    }, []);

    return (
        <>
            <div
                className={classNames('expandable-text-container', className)}
                style={{ lineHeight: `${lineHeight}px` }}
                ref={containerRef}
            >
                <div className="expandable-text-hide-container" ref={el => setEllipsisByInit(el)}>
                    {content}
                </div>
                {content}
            </div>
            <div
                ref={el => {
                    openRef.current = el;
                    if (el && showOpen.current) {
                        el.style.display = 'flex';
                    }
                }}
                className="expandable-text__expand-button"
                onClick={ev => handleExpand('open', ev)}
            >
                {tCommon.t('expand', '展开')}
            </div>
            <div
                ref={el => {
                    closeRef.current = el;
                }}
                className="expandable-text__close-button"
                onClick={ev => handleExpand('close', ev)}
            >
                {tCommon.t('collapse', '收起')}
            </div>
        </>
    );
};

export default memo(ExpandableOrEditText);
